import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
  Activity16,
  Add16,
  Ai16,
  Analytics16,
  ArrowDown16,
  ArrowLeft16,
  ArrowsVertical16,
  Calendar16,
  Camera16,
  Certificate16,
  Chat16,
  CheckmarkFilled16,
  ChevronDown16,
  ChevronRight16,
  ChevronUp16,
  Close16,
  CloudUpload16,
  Delete16,
  Document16,
  DocumentTasks16,
  DocumentUnknown16,
  Edit16,
  Education16,
  ErrorFilled16,
  EventSchedule16,
  Events16,
  Filter16,
  Finance16,
  Flash16,
  FolderShared16,
  Home16,
  IbmKnowledgeCatalogStandard16,
  Image16,
  Information16,
  Location16,
  Locked16,
  Map16,
  Microphone16,
  MicrophoneOff16,
  Notification16,
  NotificationNew16,
  OverflowMenuVertical16,
  Pause16,
  Phone16,
  PhoneIncoming16,
  PhoneOff16,
  PhoneOutgoing16,
  Play16,
  Policy16,
  RuleTest16,
  Scales16,
  Search16,
  Security16,
  Send16,
  Settings16,
  StarFilled16,
  Task16,
  Time16,
  TrashCan16,
  Trophy16,
  Undo16,
  Upload16,
  User16,
  UserFollow16,
  UserMultiple16,
  WarningAlt16,
  WarningFilled16,
  WifiOff16,
} from '@carbon/icons';
import { IconService } from 'carbon-components-angular/icon';

/**
 * Registers every Carbon icon this app references by name (either directly,
 * like `pause`/`microphone--off`, or indirectly via a carbon-components-angular
 * component's own template, like the DataTable sort arrows). IconService
 * throws IconNameNotFoundError for anything unregistered; IconDirective
 * catches that and only logs a console warning, so a missing icon fails
 * silently as a blank <svg> — easy to miss without zooming in on a
 * screenshot. Provided in both app.config.ts and Storybook's preview.ts so
 * the same icon set is registered everywhere the app actually renders.
 *
 * IconService has no `providedIn: 'root'` — it's only provided via
 * IconModule's own module providers (an `@Optional() @SkipSelf()` factory
 * that reuses a parent-provided instance if one exists, or creates one).
 * Registering it here explicitly makes the root injector that owner, so
 * every later `IconModule` import elsewhere in the tree reuses this same
 * singleton instead of creating its own with an empty cache.
 */
export function provideCarbonIcons(): EnvironmentProviders {
  return makeEnvironmentProviders([
    IconService,
    provideAppInitializer(() => {
      inject(IconService).registerAll([
        Activity16,
        Add16,
        Ai16,
        Analytics16,
        ArrowDown16,
        ArrowLeft16,
        ArrowsVertical16,
        Calendar16,
        Camera16,
        Certificate16,
        Chat16,
        CheckmarkFilled16,
        ChevronDown16,
        ChevronRight16,
        ChevronUp16,
        Close16,
        CloudUpload16,
        Delete16,
        Document16,
        DocumentTasks16,
        DocumentUnknown16,
        Edit16,
        Education16,
        ErrorFilled16,
        EventSchedule16,
        Events16,
        Filter16,
        Finance16,
        Flash16,
        FolderShared16,
        Home16,
        IbmKnowledgeCatalogStandard16,
        Image16,
        Information16,
        Location16,
        Locked16,
        Map16,
        Microphone16,
        MicrophoneOff16,
        Notification16,
        NotificationNew16,
        OverflowMenuVertical16,
        Pause16,
        Phone16,
        PhoneIncoming16,
        PhoneOff16,
        PhoneOutgoing16,
        Play16,
        Policy16,
        RuleTest16,
        Scales16,
        Search16,
        Security16,
        Send16,
        Settings16,
        StarFilled16,
        Task16,
        Time16,
        TrashCan16,
        Trophy16,
        Undo16,
        Upload16,
        UserFollow16,
        UserMultiple16,
        User16,
        WarningAlt16,
        WarningFilled16,
        WifiOff16,
      ]);
    }),
  ]);
}
